import { useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';

import { Building2, Check, ChevronsUpDown, Search } from 'lucide-react';
import { useAppDispatch } from 'shared/hooks/redux';

import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '../../../components/ui/sidebar';
import { Skeleton } from '../../../components/ui/skeleton';
import { useGetProjectsQuery } from '../api/projects';
import { getMasterProjects, matchesProjectSearch } from '../model/projectTree';
import { clearSelectedProject, setSelectedProject, useSelectedProjectId } from '../model/selectedProject';

const getFocusableMenuItems = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>('[data-slot="dropdown-menu-item"]:not([data-disabled])'));
};

const ProjectSwitcher = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const selectedId = useSelectedProjectId();
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuContentRef = useRef<HTMLDivElement>(null);
  const { data: projects = [], isLoading } = useGetProjectsQuery();
  const masterProjects = getMasterProjects(projects);
  const selected = masterProjects.find((p) => p.id === selectedId) ?? null;

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim();
    if (!query) return masterProjects;
    return masterProjects.filter((project) => matchesProjectSearch(query, project));
  }, [masterProjects, searchQuery]);

  const handleDesktopOpenChange = (nextOpen: boolean): void => {
    setDesktopOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  const handleMobileOpenChange = (nextOpen: boolean): void => {
    setMobileOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  const focusFirstMenuItem = (): void => {
    const items = getFocusableMenuItems(menuContentRef.current);
    items[0]?.focus();
  };

  const focusSearchInput = (): void => {
    menuContentRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      focusFirstMenuItem();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      focusFirstMenuItem();
      return;
    }

    if (event.key === 'Escape') return;

    event.stopPropagation();
  };

  const handleMenuItemKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const items = getFocusableMenuItems(menuContentRef.current);
    const currentIndex = items.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    if (event.key === 'ArrowUp' && currentIndex === 0) {
      const searchInput = menuContentRef.current?.querySelector<HTMLInputElement>('input');
      if (!searchInput) return;

      event.preventDefault();
      event.stopPropagation();
      searchInput.focus();
      return;
    }

    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      if (currentIndex === 0) {
        event.preventDefault();
        event.stopPropagation();
        focusSearchInput();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      items[currentIndex - 1]?.focus();
      return;
    }

    if (currentIndex < items.length - 1) {
      event.preventDefault();
      event.stopPropagation();
      items[currentIndex + 1]?.focus();
    }
  };

  const handleProjectSelect = (projectId: number): void => {
    dispatch(setSelectedProject(projectId));
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setDesktopOpen(false);
    }
    void navigate(`/project/${projectId}/config`);
  };

  const handleClearSelection = (): void => {
    dispatch(clearSelectedProject());
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setDesktopOpen(false);
    }
    void navigate('/project');
  };

  const selectorButton = (
    <SidebarMenuButton
      size="lg"
      className="bg-white/[0.06] border border-sidebar-border data-[state=open]:bg-white/10 data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center"
    >
      <Building2 className="shrink-0 text-brand-turquoise" />
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-body text-[11px] uppercase tracking-wide">Inmobiliaria</span>
        {isLoading ? (
          <Skeleton className="h-3.5 w-24 mt-0.5" />
        ) : (
          <span className="truncate font-semibold text-white">{selected?.name ?? 'Seleccionar…'}</span>
        )}
      </div>
      <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
    </SidebarMenuButton>
  );

  const searchInput = (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        onKeyDown={isMobile ? undefined : handleSearchKeyDown}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        placeholder="Buscar por nombre o ID…"
        className={isMobile ? 'h-9 pl-8 text-sm' : 'h-7 pl-8 text-xs'}
        autoFocus
      />
    </div>
  );

  const projectItems = isLoading ? (
    <DropdownMenuItem disabled>Cargando inmobiliarias…</DropdownMenuItem>
  ) : (
    filteredProjects.map((p) => (
      <DropdownMenuItem
        key={p.id}
        onSelect={() => handleProjectSelect(p.id)}
        onKeyDown={isMobile ? undefined : handleMenuItemKeyDown}
      >
        <span className="truncate">
          {p.name ?? 'Sin nombre'} <span className="text-muted-foreground text-xs font-normal">({p.id})</span>
        </span>
        {p.id === selectedId && <Check className="ml-auto size-4 shrink-0" />}
      </DropdownMenuItem>
    ))
  );

  const mobileProjectItems = isLoading ? (
    <div className="p-2 text-sm text-muted-foreground">Cargando inmobiliarias…</div>
  ) : (
    filteredProjects.map((p) => (
      <button
        key={p.id}
        type="button"
        className="flex w-full items-center gap-1.5 rounded-md p-2 text-left text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
        onClick={() => handleProjectSelect(p.id)}
      >
        <span className="truncate">
          {p.name ?? 'Sin nombre'} <span className="text-muted-foreground text-xs font-normal">({p.id})</span>
        </span>
        {p.id === selectedId && <Check className="ml-auto size-4 shrink-0" />}
      </button>
    ))
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {isMobile ? (
          <Sheet open={mobileOpen} onOpenChange={handleMobileOpenChange}>
            <SheetTrigger asChild>{selectorButton}</SheetTrigger>
            <SheetContent side="left" className="w-[min(100vw,24rem)] gap-0 p-0">
              <SheetHeader className="border-b border-border pr-14">
                <SheetTitle>Inmobiliarias</SheetTitle>
                <SheetDescription>Selecciona la inmobiliaria activa del Ops Center.</SheetDescription>
              </SheetHeader>
              <div className="border-b border-border p-4">{searchInput}</div>
              <div className="flex-1 overflow-y-auto p-1">
                {mobileProjectItems}
                {!isLoading && masterProjects.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">Sin inmobiliarias disponibles</div>
                )}
                {!isLoading && masterProjects.length > 0 && filteredProjects.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">Sin resultados</div>
                )}
                {selectedId !== null && (
                  <>
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      className="flex w-full items-center rounded-md p-2 text-left text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                      onClick={handleClearSelection}
                    >
                      Limpiar selección
                    </button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <DropdownMenu open={desktopOpen} onOpenChange={handleDesktopOpenChange}>
            <DropdownMenuTrigger asChild>{selectorButton}</DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="start"
              className="w-64"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <div ref={menuContentRef}>
                <DropdownMenuLabel>Inmobiliarias</DropdownMenuLabel>
                {!isLoading && masterProjects.length > 0 && (
                  <div className="sticky top-0 z-10 bg-popover px-1 pb-1">{searchInput}</div>
                )}
                {projectItems}
                {!isLoading && masterProjects.length === 0 && (
                  <DropdownMenuItem disabled>Sin inmobiliarias disponibles</DropdownMenuItem>
                )}
                {!isLoading && masterProjects.length > 0 && filteredProjects.length === 0 && (
                  <DropdownMenuItem disabled>Sin resultados</DropdownMenuItem>
                )}
                {selectedId !== null && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={handleClearSelection} onKeyDown={handleMenuItemKeyDown}>
                      Limpiar selección
                    </DropdownMenuItem>
                  </>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default ProjectSwitcher;
